import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Inbox } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { DetalleReporte } from "@/componentes/soporte/DetalleReporte"
import { Cajon } from "@/componentes/ui/Cajon"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha } from "@/lib/fechas"

/**
 * La bandeja de desarrollo: lo que la gente reportó, y qué se hace con ello.
 *
 * ⚠ **Aquí llega TODO, incluido lo que el clasificador marcó como el sistema
 * negándose a propósito.** El bot no cierra nada. Un `RECHAZO_ESPERADO` puede ser un
 * defecto igual —una compuerta documental que pide un documento ya subido emite
 * `DOCUMENTOS_FALTANTES` y es un fallo de verdad—, y filtrarlo aquí sería perder en
 * silencio la clase de fallo más cara.
 *
 * ⚠ **El veredicto va SIEMPRE con su motivo.** Una etiqueta sin la frase que la
 * explica no se puede discutir; con ella se ve qué evidencia la produjo y se puede
 * decir que el clasificador se equivocó.
 */

export type Resumen = {
  id: number
  reportante_id: number
  vista: string
  titulo: string
  veredicto: "FALLA" | "RECHAZO_ESPERADO" | "INDETERMINADO"
  confianza: string
  estado: "NUEVO" | "ACEPTADO" | "DESCARTADO"
  tarea_id: number | null
  tiene_captura: boolean
  creado_en: string
}

const TONO_VEREDICTO = {
  FALLA: "peligro",
  RECHAZO_ESPERADO: "neutro",
  INDETERMINADO: "aviso",
} as const

const NOMBRE_VEREDICTO = {
  FALLA: "Falla",
  RECHAZO_ESPERADO: "El sistema se negó",
  INDETERMINADO: "Por confirmar",
}

const TONO_ESTADO = { NUEVO: "primario", ACEPTADO: "exito", DESCARTADO: "neutro" } as const

const FILTROS = [
  ["NUEVO", "Sin triar"],
  ["", "Todos"],
  ["ACEPTADO", "Aceptados"],
  ["DESCARTADO", "Descartados"],
] as const

export function Reportes() {
  const clienteQuery = useQueryClient()
  const [estado, setEstado] = useState<string>("NUEVO")
  const [abierto, setAbierto] = useState<number | null>(null)

  const reportes = useQuery({
    queryKey: ["reportes", estado],
    queryFn: () =>
      api.get<Resumen[]>(`/api/v1/reportes${estado === "" ? "" : `?estado=${estado}`}`),
  })

  const triar = useMutation({
    mutationFn: ({ id, acepta, resolucion }: { id: number; acepta: boolean; resolucion: string }) =>
      api.post(`/api/v1/reportes/${id}/triaje`, { acepta, resolucion }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["reportes"] })
      setAbierto(null)
    },
  })

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Reportes de falla</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {reportes.data ? `${reportes.data.length} reportes` : " "}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTROS.map(([valor, texto]) => (
            <button
              key={texto}
              type="button"
              onClick={() => setEstado(valor)}
              aria-pressed={estado === valor}
              className={
                estado === valor
                  ? "boton-primario h-8 px-3 text-xs"
                  : "boton-secundario h-8 px-3 text-xs"
              }
            >
              {texto}
            </button>
          ))}
        </div>
      </div>

      {reportes.isPending && (
        <p role="status" className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}

      {reportes.isError && (
        <p
          role="alert"
          className="py-12 text-center text-sm"
          style={{ color: "var(--destructive)" }}
        >
          {reportes.error instanceof ErrorApi ? reportes.error.message : "No se pudo cargar"}
        </p>
      )}

      {reportes.data?.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 superficie py-16"
          style={{ color: "var(--fg-muted)" }}
        >
          <Inbox size={24} aria-hidden={true} />
          <p className="text-sm">Nada por aquí.</p>
        </div>
      )}

      <ul className="space-y-2">
        {(reportes.data ?? []).map((r) => (
          <li key={r.id} data-testid={`reporte-${r.id}`} className="superficie p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Insignia tono={TONO_VEREDICTO[r.veredicto]}>
                {NOMBRE_VEREDICTO[r.veredicto]}
              </Insignia>
              <Insignia tono={TONO_ESTADO[r.estado]}>{r.estado}</Insignia>
              <button
                type="button"
                onClick={() => setAbierto(r.id)}
                data-testid={`abrir-${r.id}`}
                className="flex-1 text-left text-sm font-medium underline-offset-2 hover:underline"
              >
                {r.titulo}
              </button>
              <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                {r.vista} · {comoFecha(r.creado_en)}
              </span>
              {r.tarea_id !== null && (
                <Link to="/tareas" className="text-xs underline-offset-2 hover:underline">
                  Tarea #{r.tarea_id}
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Cajon abierto={abierto !== null} titulo="Reporte" onCerrar={() => setAbierto(null)}>
        {abierto !== null && (
          <DetalleReporte
            key={abierto}
            reporteId={abierto}
            triando={triar.isPending}
            error={triar.error}
            onTriar={(acepta, resolucion) => triar.mutate({ id: abierto, acepta, resolucion })}
          />
        )}
      </Cajon>
    </div>
  )
}
